import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — ĐIỀU HÀNH: CHẶNG · ẢNH POD (F12) ═══════════════════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/az/legs` và `/api/v1/az/attachments`.
 */

export const LegStatus = z.enum([
  'new', 'assigned', 'at_pickup', 'loaded', 'in_transit', 'at_drop', 'delivered', 'cancelled',
])
export type LegStatus = z.infer<typeof LegStatus>

const legFields = {
  name: z.string().optional(),
  vendorId: blankAsAbsent(Uuid),
  truckPlate: z.string().optional(),
  driveLink: z.string().optional(),
  note: z.string().optional(),
}

export const CreateLegBody = z.object({
  jobId: Uuid,
  fromAddr: z.string().min(1, 'Chưa nhập điểm đi của chặng.'),
  toAddr: z.string().min(1, 'Chưa nhập điểm đến của chặng.'),
  planDate: ISODate,
  ...legFields,
})

export const UpdateLegBody = z.object({
  version: Version,
  fromAddr: blankAsAbsent(z.string()),
  toAddr: blankAsAbsent(z.string()),
  planDate: blankAsAbsent(ISODate),
  ...legFields,
})

/**
 * Đổi trạng thái chặng. `'next'` = đi tới bước kế tiếp trong dãy — bản cũ chỉ có nút "→" chứ không
 * cho nhảy cóc (`advance`, `Az.tsx:9701`); nhận thẳng một trạng thái là để điều hành sửa sai.
 */
export const LegStatusBody = z.object({
  version: Version,
  status: z.union([LegStatus, z.literal('next')]),
})

export const ConfirmLegBody = z.object({
  version: Version,
  via: z.enum(['vendor', 'company']),
})

export const RejectLegBody = z.object({
  version: Version,
  reason: z.string().min(1, 'Phải ghi lý do từ chối — đây là số liệu đánh giá thầu phụ.'),
})

export const LegStepBody = z.object({ version: Version })

/** Một dòng lịch sử chặng (lý do khi đơn chốt giao mà chặng chưa xong). */
export const LegNoteBody = z.object({ version: Version, note: z.string().min(1, 'Chưa ghi nội dung.').max(1000) })

export const ListLegQuery = z.object({ jobId: Uuid })

/**
 * CHẶNG THEO KHOẢNG NGÀY — cửa của 📅 Lịch chạy và 📊 Báo cáo vận hành.
 *
 * ⚠ CỬA RIÊNG, KHÔNG nới `ListLegQuery` thành "jobId HOẶC from/to". Một lược đồ mà mọi trường
 *   đều tuỳ chọn thì gọi hụt cả hai cũng hợp lệ, và khi đó máy chủ trả CẢ BẢNG chặng. Hai câu
 *   hỏi khác nhau ⇒ hai cửa, mỗi cửa có đúng ràng buộc của nó.
 */
export const LegRangeQuery = z.object({ from: ISODate, to: ISODate })

/* ══ CỔNG THẦU PHỤ (F13) ══════════════════════════════════════════════════════════════════════
 * KHÔNG có `vendorId` trong bất kỳ lược đồ nào dưới đây, và đó là điều quan trọng nhất của cả
 * nhóm: thầu phụ KHÔNG được nói mình là ai — máy chủ lấy `vendorId` từ PHIÊN. Nhận nó từ thân
 * request là mời mọi thầu phụ đọc việc của nhau bằng một lần sửa JSON.
 */
export const MyLegQuery = PageQuery
/* ⛔ KHÔNG có lược đồ "thầu phụ đẩy tiến độ chặng" — cửa đó gỡ theo bản cũ (người dùng chốt 17/10, C14). */
/** `truckPlate` — BKS gõ sẵn lúc nhận chuyến, lưu cùng lượt (bản cũ `confirmLeg`, `NccPortal.tsx:259-267`). */
export const MyLegConfirmBody = z.object({ version: Version, truckPlate: blankAsAbsent(z.string().max(32)) })
export const MyLegRejectBody = RejectLegBody
/** ⭐ Chuỗi RỖNG = XOÁ BKS (L32, soát 16/10; bản cũ `saveBks` ghi "(xóa)", `NccPortal.tsx:269-272`). */
export const MyLegPlateBody = z.object({
  version: Version,
  truckPlate: z.string().max(32),
})
export const MyPodBody = z.object({
  legId: Uuid,
  fileName: z.string().min(1, 'Thiếu tên tệp.'),
  mime: z.string().min(1, 'Thiếu loại tệp.'),
  dataBase64: z.string().min(1, 'Tệp rỗng.'),
})

export const LegIdParams = z.object({ id: Uuid })

/**
 * Cont/Seal thầu phụ tự nhập trên cổng — ghi lên ĐƠN (bản cũ `saveContSeal`, `NccPortal.tsx:82`).
 * ⚠ Hai ô BẮT BUỘC có mặt (chuỗi rỗng = xoá): bản cũ luôn gửi cả hai, và "vắng mặt" ở đây không có
 *   nghĩa gì khác "để trống".
 */
export const MyContSealBody = z.object({
  contNo: z.string().max(32),
  sealNo: z.string().max(32),
})

/** 📋 Kế hoạch ▸ xác nhận nhận trước MỘT hay NHIỀU đơn một lượt (bản cũ `confirmPlan` / `confirmPlanBulk`). */
export const MyPlanConfirmBody = z.object({
  jobIds: z.array(Uuid).min(1, 'Chưa chọn đơn nào.').max(200),
})

/**
 * TẢI TỆP LÊN — thân JSON mang nội dung dạng base64.
 *
 * ⚠ KHÔNG dùng `multipart/form-data`: nó cần thêm một gói và một lớp phân giải riêng, cho một
 *   luồng duy nhất là ảnh POD. Base64 phình 4/3 lần, nên route đó tự nới `bodyLimit` (trần chung
 *   256 KB giữ nguyên cho mọi route khác — xem `server.ts`).
 * ⚠ Trần 5 MB kiểm ở BA nơi: `bodyLimit` của route · luật miền `attachmentError` · `CHECK` của
 *   CSDL. Ba lớp cho một con số nghe thừa, nhưng lớp ngoài cùng chặn trước khi tốn bộ nhớ, còn
 *   lớp trong cùng là thứ duy nhất không ai lách được.
 */
export const UploadAttachmentBody = z.object({
  ownerKind: z.enum(['job', 'leg', 'contract', 'cont_repair', 'cont_fuel_log', 'cont_fuel_transfer', 'ap_statement', 'payment_request', 'bank_txn']),
  ownerId: Uuid,
  kind: z.string().optional(),
  fileName: z.string().min(1, 'Thiếu tên tệp.'),
  mime: z.string().min(1, 'Thiếu loại tệp.'),
  dataBase64: z.string().min(1, 'Tệp rỗng.'),
})

export const ListAttachmentQuery = PageQuery.extend({
  ownerKind: z.enum(['job', 'leg', 'contract', 'cont_repair', 'cont_fuel_log', 'cont_fuel_transfer', 'ap_statement', 'payment_request', 'bank_txn']),
  ownerId: Uuid,
})

export const AttachmentIdParams = z.object({ id: Uuid })

/**
 * Gỡ một tệp. Thân RỖNG nhưng vẫn phải khai lược đồ — cổng `route-phai-khai-luoc-do` đòi mọi route
 * ghi có lược đồ thân, và `.strict()` ở đây chặn luôn việc ai đó lén gửi thêm trường.
 * ⚠ KHÔNG có `version`: tệp đính kèm không có cột đó — nó bất biến, chỉ thêm hoặc gỡ.
 */
export const AttachmentDeleteBody = z.object({}).strict()

export type ListLegQueryT = z.infer<typeof ListLegQuery>
export type LegNoteBodyT = z.infer<typeof LegNoteBody>
export type LegRangeQueryT = z.infer<typeof LegRangeQuery>
export type ListAttachmentQueryT = z.infer<typeof ListAttachmentQuery>
export type UploadAttachmentBodyT = z.infer<typeof UploadAttachmentBody>
/* ══ ✏️ SỬA CHẶNG ĐÃ BẮN (`EditLegsModal`) — mỗi ô gửi riêng, máy chủ kiểm khoá lại trước khi ghi ══════════════════════ */

export const LegEditBody = z.object({
  version: Version,
  jobId: Uuid,
  vendorId: blankAsAbsent(Uuid),
  /** Mã thầu phụ để ghi lịch sử "Đổi thầu phụ → …" — chỉ là nhãn, không quyết định gì. */
  vendorCode: z.string().optional(),
  fromAddr: z.string().optional(),
  toAddr: z.string().optional(),
  truckPlate: z.string().optional(),
  actualDeliveredAt: blankAsAbsent(ISODate),
  /** Giá mua cước của chặng — sửa tay ⇒ giá thoả thuận, bỏ ghim giá thầu. */
  freightAmount: blankAsAbsent(moneyField('Giá mua')),
})

export const LegEditStepBody = z.object({ version: Version, jobId: Uuid })

export const LegEditAddSurchargeBody = z.object({
  legId: Uuid,
  label: z.string().trim().min(1, 'Chưa nhập tên phụ phí.'),
  amount: moneyField('Số tiền phụ phí'),
})

export const LegEditContInfoBody = z.object({
  contNo: z.string().optional(),
  sealNo: z.string().optional(),
  contType: z.string().optional(),
  trainNo: z.string().optional(),
})

export const LegEditOrderParams = z.object({ id: Uuid })
export const LegEditBodyEmpty = z.object({})

export type LegEditBodyT = z.infer<typeof LegEditBody>
export type LegEditStepBodyT = z.infer<typeof LegEditStepBody>
export type LegEditAddSurchargeBodyT = z.infer<typeof LegEditAddSurchargeBody>
export type LegEditContInfoBodyT = z.infer<typeof LegEditContInfoBody>
export type UpdateLegBodyT = z.infer<typeof UpdateLegBody>
